/**
 * Selectors API hooks (Wave 2.27 FE, TASK-078a1 + TASK-078a2).
 *
 * Endpoint contract (`apps/api/src/selectors/selectors.controller.ts`):
 *   GET    /admin/selectors                  → SelectorListResponse
 *   GET    /admin/selectors/:id              → SelectorCard
 *   PATCH  /admin/selectors/:id              → { id, changes }
 *   POST   /admin/selectors/:id/block        → { id, status, blocked_at }
 *   POST   /admin/selectors/:id/unblock      → { id, status, blocked_at: null }
 *
 * BUG-023 (Variant 1) — `POST /admin/selectors` was REMOVED from BE.
 * Selector creation lives behind `POST /admin/settings/admins/invite`
 * (role='selector' branch) — see `api/admins.ts` / `useInviteAdminMutation`.
 *
 * RBAC: backend enforces `@AdminOrAbove()` (admin + super_admin). Selectors
 * are a staff-only domain — no `account_id` scoping. The FE route guard
 * mirrors this gate so client roles never see the page.
 *
 * Wire DTOs come straight from `@music-app/shared` (`SelectorListResponse`,
 * `SelectorCard`, `SelectorPatch`, `SelectorFilter`).
 * NO local shim — every DTO is canonical.
 *
 * Pagination: keyset, `cursor` opaque base64url token. Cursor is invalidated
 * by any filter change.
 *
 * Invalidation strategy:
 *   - usePatchSelectorMutation  → invalidates ['selector-card', id] +
 *     ['selectors', 'list'] (the list row shows name/status that may shift).
 *   - useBlockSelectorMutation / useUnblockSelectorMutation → same as patch.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  AuditLogListResponse,
  SelectorCard,
  SelectorInviteResponse,
  SelectorListResponse,
  SelectorPatch,
  SelectorStatsResponse,
  SelectorStatus,
  TrackListResponse,
  TrackStatus,
} from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const selectorsListQueryKey = (params: SelectorsListQueryParams) =>
  ['selectors', 'list', params] as const;
export const selectorCardQueryKey = (id: string) =>
  ['selector-card', id] as const;
export const selectorAuditQueryKey = (
  id: string,
  params: SelectorAuditQueryParams,
) => ['selector-audit', id, params] as const;
export const selectorTracksQueryKey = (
  id: string,
  params: SelectorTracksQueryParams,
) => ['selector-tracks', id, params] as const;
export const selectorStatsQueryKey = (id: string) =>
  ['selector-stats', id] as const;

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
export interface SelectorsListFilters {
  q?: string;
  status?: SelectorStatus;
}

export interface SelectorsListQueryParams {
  filters?: SelectorsListFilters;
  cursor?: string;
  limit?: number;
}

/** Page size used both on the wire (`limit=`) and to size the UI footer.
 * Matches `selectorFilterSchema.limit.default`. */
export const SELECTORS_PAGE_SIZE = 50;
const SELECTORS_ENDPOINT = '/admin/selectors';

function buildSelectorsSearchParams(
  params: SelectorsListQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.filters?.q) sp.set('q', params.filters.q);
  if (params.filters?.status) sp.set('status', params.filters.status);
  sp.set('limit', String(params.limit ?? SELECTORS_PAGE_SIZE));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

export function useSelectorsListQuery(
  params: SelectorsListQueryParams,
  options?: { enabled?: boolean },
) {
  return useQuery<SelectorListResponse>({
    queryKey: selectorsListQueryKey(params),
    queryFn: () =>
      apiClient<SelectorListResponse>(
        `${SELECTORS_ENDPOINT}?${buildSelectorsSearchParams(params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
export function useSelectorCardQuery(id: string | undefined) {
  return useQuery<SelectorCard>({
    queryKey: selectorCardQueryKey(id ?? ''),
    queryFn: () => apiClient<SelectorCard>(`/admin/selectors/${id}`),
    staleTime: STALE_TIME.single,
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Stats (admin-scoped — GET /admin/selectors/:id/stats, Wave 2.46 BE-2)
//
// NOTE (BUG-023): the cabinet-side `/selector/stats/{summary,earnings,
// activity}` endpoints are scoped to the caller's own user_id and cannot
// be reused for admins viewing another selector's card. The admin-scoped
// summary covers four of the canonical tiles (presets / promos / total
// earned / last active). The remaining surfaces — tracks_approved,
// period-bucketed earnings series, moderation-event feed — are tracked
// under TASK-BUG-023-followup-be-admin-selector-stats.
// ---------------------------------------------------------------------------
export function useSelectorStatsQuery(id: string | undefined) {
  return useQuery<SelectorStatsResponse>({
    queryKey: selectorStatsQueryKey(id ?? ''),
    queryFn: () =>
      apiClient<SelectorStatsResponse>(`/admin/selectors/${id}/stats`),
    staleTime: STALE_TIME.single,
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Mutations
//
// Each mutation awaits its invalidations before resolving so the FE M-1
// invariant holds: callers can `await mutateAsync(…)` and only then close
// the modal — by the time the modal unmounts the next list/card fetch is
// already in flight.
// ---------------------------------------------------------------------------

interface PatchAck {
  id: string;
  changes: Record<string, { from: unknown; to: unknown }>;
}

export function usePatchSelectorMutation(
  id: string,
): UseMutationResult<PatchAck, Error, SelectorPatch> {
  const queryClient = useQueryClient();
  return useMutation<PatchAck, Error, SelectorPatch>({
    mutationFn: (patch) =>
      apiClient<PatchAck>(`/admin/selectors/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: selectorCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['selectors', 'list'] }),
      ]);
    },
  });
}

interface BlockAck {
  id: string;
  status: SelectorStatus;
  blocked_at: string | null;
}

export function useBlockSelectorMutation(
  id: string,
): UseMutationResult<BlockAck, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<BlockAck, Error, void>({
    mutationFn: () =>
      apiClient<BlockAck>(`/admin/selectors/${id}/block`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: selectorCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['selectors', 'list'] }),
      ]);
    },
  });
}

export function useUnblockSelectorMutation(
  id: string,
): UseMutationResult<BlockAck, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<BlockAck, Error, void>({
    mutationFn: () =>
      apiClient<BlockAck>(`/admin/selectors/${id}/unblock`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: selectorCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['selectors', 'list'] }),
      ]);
    },
  });
}

// ---------------------------------------------------------------------------
// Resend invite (BUG-108 — POST /admin/selectors/:id/resend-invite)
//
// Mints a fresh single-use invite token (invalidating any prior live one)
// and re-mails the invite link. In dev/staging the link is echoed back via
// `invite_link_dev_only` so the operator can copy it without a live SMTP
// relay; never set in production. The selector card "Приглашения" tab calls
// this and surfaces the returned link with a copy button.
//
// Invalidates the card query so a freshly-onboarded selector's
// `last_login_at` (and hence the pending/active split) re-renders.
// ---------------------------------------------------------------------------
export function useResendSelectorInviteMutation(
  id: string,
): UseMutationResult<SelectorInviteResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<SelectorInviteResponse, Error, void>({
    mutationFn: () =>
      apiClient<SelectorInviteResponse>(
        `/admin/selectors/${id}/resend-invite`,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: selectorCardQueryKey(id),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Selector-scoped audit
//
// No dedicated `/admin/selectors/:id/audit` endpoint exists — we reach the
// data through the global `/admin/audit` route with the canonical filter
// pair `entity_type=selector&entity_id=<selectorId>`. Same pattern as
// `useVenueAuditQuery` (Wave 2.26 FE, TASK-075c). Pagination is keyset;
// ordering is fixed server-side to `(occurred_at DESC, id DESC)`.
// ---------------------------------------------------------------------------

export interface SelectorAuditQueryParams {
  action?: string;
  date_from?: string;
  date_to?: string;
  cursor?: string;
  limit?: number;
}

export const SELECTOR_AUDIT_PAGE_SIZE = 50;

function buildSelectorAuditSearchParams(
  selectorId: string,
  params: SelectorAuditQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('entity_type', 'selector');
  sp.set('entity_id', selectorId);
  if (params.action) sp.set('action', params.action);
  if (params.date_from) sp.set('date_from', params.date_from);
  if (params.date_to) sp.set('date_to', params.date_to);
  if (params.cursor) sp.set('cursor', params.cursor);
  sp.set('limit', String(params.limit ?? SELECTOR_AUDIT_PAGE_SIZE));
  return sp;
}

export function useSelectorAuditQuery(
  id: string | undefined,
  params: SelectorAuditQueryParams,
) {
  return useQuery<AuditLogListResponse>({
    queryKey: selectorAuditQueryKey(id ?? '', params),
    queryFn: () =>
      apiClient<AuditLogListResponse>(
        `/admin/audit?${buildSelectorAuditSearchParams(id ?? '', params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    enabled: Boolean(id),
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Selector-scoped tracks (Wave 2.28 FE, TASK-078a3)
//
// No dedicated `/admin/selectors/:id/tracks` endpoint — we reuse the global
// `GET /admin/tracks` list with `selector_id=<selectorId>`. The wire filter
// (`trackFilterSchema.selector_id`) is in shared since Wave 2.4; the service
// scopes results to `tracks.uploaded_by = selector_id`. Keyset pagination
// (`cursor`/`next_cursor`) per Wave 2.6 review M-5 — `total` is null in this
// mode, but we don't need a row-count header in the tab.
//
// `q` is a free-text title/artist search forwarded as-is. `status_in` accepts
// a single `TrackStatus` here (the tab UI does not surface multi-select); we
// project it onto the CSV-list parameter the BE expects.
// ---------------------------------------------------------------------------

export interface SelectorTracksFilters {
  q?: string;
  status?: TrackStatus;
}

export interface SelectorTracksQueryParams {
  filters?: SelectorTracksFilters;
  cursor?: string;
  limit?: number;
}

export const SELECTOR_TRACKS_PAGE_SIZE = 50;

function buildSelectorTracksSearchParams(
  selectorId: string,
  params: SelectorTracksQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('selector_id', selectorId);
  if (params.filters?.q) sp.set('q', params.filters.q);
  if (params.filters?.status) sp.set('status_in', params.filters.status);
  sp.set('size', String(params.limit ?? SELECTOR_TRACKS_PAGE_SIZE));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

export function useSelectorTracksQuery(
  id: string | undefined,
  params: SelectorTracksQueryParams,
) {
  return useQuery<TrackListResponse>({
    queryKey: selectorTracksQueryKey(id ?? '', params),
    queryFn: () =>
      apiClient<TrackListResponse>(
        `/admin/tracks?${buildSelectorTracksSearchParams(id ?? '', params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    enabled: Boolean(id),
    placeholderData: (prev) => prev,
  });
}
